import { useTranslation } from "react-i18next";
import { cx } from "../cx";

/**
 * A 14px ring for inline waiting states. On its own it is a live region that
 * announces "Lädt …", which is what a bare spinner in a card needs.
 *
 * `decorative` is for a spinner inside a control that already says it is busy:
 * a Button carries `aria-busy` and its own sr-only busy text, a PlayButton its
 * `aria-label`, and a second live region inside them would announce twice and
 * push the spinner's label into the control's accessible name. The flag is a
 * real prop rather than an `aria-hidden` the caller passes, because this
 * component does not spread rest props and a hyphenated JSX attribute would be
 * dropped without a type error.
 */
export function InlineSpinner({
  className = "",
  size = 14,
  decorative = false,
}: {
  className?: string;
  size?: number;
  decorative?: boolean;
}) {
  const { t } = useTranslation("components");
  const announcement = decorative
    ? { "aria-hidden": true }
    : { role: "status", "aria-label": t("spinner.label") };
  return (
    <span
      {...announcement}
      data-component="InlineSpinner"
      data-decorative={decorative || undefined}
      className={cx("inline-block shrink-0 animate-spin rounded-full border-2", className)}
      style={{
        width: size,
        height: size,
        borderColor: "var(--color-line)",
        borderTopColor: "currentColor",
      }}
    />
  );
}
